import { useMutation, useQuery } from '@tanstack/react-query';
import fitStudioRequest from '../utils/request';

const fitCheckingServices = {
  // ==================== Branch ====================
  useQueryGetBranches({ take, page, searchText }, onSuccess) {
    return useQuery(
      ['get-checking-branches', { take, page, searchText }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/legacy-checkin-branch`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              ...(queryKey[1].searchText && {
                searchText: queryKey[1].searchText,
              }),
            },
          });
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetBranchById({ branchId }, onSuccess) {
    return useQuery(
      ['get-checking-branch-by-id', { branchId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/${queryKey[1].branchId}`,
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!branchId,
      },
    );
  },
  useQueryGetBranchSummary({ branchId, month, year }, onSuccess) {
    return useQuery(
      ['get-checking-branch-summary', { branchId, month, year }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/${queryKey[1].branchId}/summary`,
            {
              params: {
                month: queryKey[1].month,
                year: queryKey[1].year,
              },
            },
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetBranchHistories(
    { branchId, take, page, month, year, searchText },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-checking-branch-histories',
        { branchId, take, page, month, year, searchText },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/${queryKey[1].branchId}/history`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                month: queryKey[1].month,
                year: queryKey[1].year,
                ...(queryKey[1].searchText && {
                  searchText: queryKey[1].searchText,
                }),
              },
            },
          );
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetBranchSummary2({ branchId, startDate, endDate }, onSuccess) {
    return useQuery(
      ['get-checking-branch-summary2', { branchId, startDate, endDate }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/${queryKey[1].branchId}/summary2`,
            {
              params: {
                ...(queryKey[1].startDate && {
                  startDate: queryKey[1].startDate,
                }),
                ...(queryKey[1].endDate && { endDate: queryKey[1].endDate }),
              },
            },
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetBranchHistories2(
    { branchId, take, page, startDate, endDate, searchText },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-checking-branch-histories2',
        { branchId, take, page, startDate, endDate, searchText },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/${queryKey[1].branchId}/history2`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                ...(queryKey[1].startDate && {
                  startDate: queryKey[1].startDate,
                }),
                ...(queryKey[1].endDate && { endDate: queryKey[1].endDate }),
                ...(queryKey[1].searchText && {
                  searchText: queryKey[1].searchText,
                }),
              },
            },
          );
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useMutationDownloadBranchHistory2Excel(onSuccess) {
    return useMutation({
      mutationFn: async (data) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch/download-excel/history2/${data.branchId}`,
            {
              params: {
                ...(data.startDate && { startDate: data.startDate }),
                ...(data.endDate && { endDate: data.endDate }),
                ...(data.searchText && { searchText: data.searchText }),
                page: '1',
                take: '-1',
              },
              responseType: 'blob',
            },
          );

          if (res.status === 200) {
            const href = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'checkin-history.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          return res.data;
        } catch (error) {
          throw error.response?.data?.message || error.message;
        }
      },
      onSuccess,
    });
  },
  // ==================== Branch Activity ====================
  useQueryGetBranchActivities({ take, page, searchText, branchId }, onSuccess) {
    return useQuery(
      ['get-checking-branch-activities', { take, page, searchText, branchId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch-activity`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                branchId: queryKey[1].branchId,
                ...(queryKey[1].searchText && {
                  searchText: queryKey[1].searchText,
                }),
              },
            },
          );
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetBranchActivityById({ activityId }, onSuccess) {
    return useQuery(
      ['get-checking-branch-activity-by-id', { activityId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-branch-activity/${queryKey[1].activityId}`,
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!activityId,
      },
    );
  },
  useMutationCreateBranchActivity(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(
            `/legacy-checkin-branch-activity`,
            data,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationUpdateBranchActivity(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/legacy-checkin-branch-activity/${data.id}`,
            data.data,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationUpdateBranchActivityDisplay(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/legacy-checkin-branch-activity/${data.id}/display`,
            { display: data.display },
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationDeleteBranchActivity(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/legacy-checkin-branch-activity/${data.id}`,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess },
    );
  },

  // ==================== User ====================
  useQueryGetTeamNames(onSuccess) {
    return useQuery(
      ['get-team-names'],
      async () => {
        try {
          const res = await fitStudioRequest.get(`/users/team-names`);
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetCheckingUsers(
    { take, page, searchText, memberType, TeamName },
    onSuccess,
  ) {
    return useQuery(
      ['get-checking-users', { take, page, searchText, memberType, TeamName }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(`/legacy-checkin-user`, {
            params: {
              take: queryKey[1].take,
              page: queryKey[1].page,
              ...(queryKey[1].searchText && {
                searchText: queryKey[1].searchText,
              }),
              ...(queryKey[1].memberType && {
                memberType: queryKey[1].memberType,
              }),
              ...(queryKey[1].TeamName && {
                TeamName: queryKey[1].TeamName,
              }),
            },
          });
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetCheckingUserById({ userId }, onSuccess) {
    return useQuery(
      ['get-checking-user-by-id', { userId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user/${queryKey[1].userId}`,
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!userId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useQueryGetCheckingUserByMemberCode({ memberCode }, onSuccess) {
    return useQuery(
      ['get-checking-user-by-member-code', { memberCode }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user/by-user`,
            {
              params: {
                ...(queryKey[1].memberCode && {
                  memberCode: queryKey[1].memberCode,
                }),
              },
            },
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!memberCode,
        refetchOnWindowFocus: false,
      },
    );
  },
  useMutationCreateCheckingUser(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(`/legacy-checkin-user`, data);
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationUpdateCheckingUser(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/legacy-checkin-user/${data.id}`,
            data.data,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationDeleteCheckingUser(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/legacy-checkin-user/${data.id}`,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess },
    );
  },

  // ==================== User History ====================
  useQueryGetCheckinUserHistories(
    { take, page, userId, branchId, month, year },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-checkin-user-histories',
        { take, page, userId, branchId, month, year },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user-history`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                ...(queryKey[1].userId && {
                  userId: queryKey[1].userId,
                }),
                ...(queryKey[1].branchId && {
                  branchId: queryKey[1].branchId,
                }),
                ...(queryKey[1].month !== undefined &&
                  queryKey[1].month !== null && {
                    month: queryKey[1].month,
                  }),
                ...(queryKey[1].year && {
                  year: queryKey[1].year,
                }),
              },
            },
          );
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetUserHistoryById({ historyId }, onSuccess) {
    return useQuery(
      ['get-checking-user-history-by-id', { historyId }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user-history/${queryKey[1].historyId}`,
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      {
        onSuccess,
        enabled: !!historyId,
        refetchOnWindowFocus: false,
      },
    );
  },
  useQueryGetUserHistorySummary({ userId, year, month }, onSuccess) {
    return useQuery(
      ['get-checking-user-history-summary', { userId, year, month }],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user-history/summary`,
            {
              params: {
                userId: queryKey[1].userId,
                year: queryKey[1].year,
                month: queryKey[1].month,
              },
            },
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetUserHistoryUserSummary(
    { startDate, endDate, branchId },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-checking-user-history-user-summary',
        { startDate, endDate, branchId },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user-history/user-summary`,
            {
              params: {
                ...(queryKey[1].startDate && {
                  startDate: queryKey[1].startDate,
                }),
                ...(queryKey[1].endDate && {
                  endDate: queryKey[1].endDate,
                }),
                ...(queryKey[1].branchId && {
                  branchId: queryKey[1].branchId,
                }),
              },
            },
          );
          return res.data.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useQueryGetUserHistoriesByUser(
    { take, page, memberCode, startDate, endDate },
    onSuccess,
  ) {
    return useQuery(
      [
        'get-checking-user-histories-by-user',
        { take, page, memberCode, startDate, endDate },
      ],
      async ({ queryKey }) => {
        try {
          const res = await fitStudioRequest.get(
            `/legacy-checkin-user-history/by-user`,
            {
              params: {
                take: queryKey[1].take,
                page: queryKey[1].page,
                ...(queryKey[1].memberCode && {
                  memberCode: queryKey[1].memberCode,
                }),
                ...(queryKey[1].startDate && {
                  startDate: queryKey[1].startDate,
                }),
                ...(queryKey[1].endDate && {
                  endDate: queryKey[1].endDate,
                }),
              },
            },
          );
          return res.data;
        } catch (error) {
          throw new Error(error.response.data.message);
        }
      },
      { onSuccess },
    );
  },
  useMutationCreateUserHistory(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.post(
            `/legacy-checkin-user-history`,
            data,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationUpdateUserHistory(onSuccess, onError) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.patch(
            `/legacy-checkin-user-history/${data.id}`,
            data.data,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess, onError },
    );
  },
  useMutationDeleteUserHistory(onSuccess) {
    return useMutation(
      async (data) => {
        try {
          const res = await fitStudioRequest.delete(
            `/legacy-checkin-user-history/${data.id}`,
          );
          return res.data.data;
        } catch (error) {
          throw error.response.data.message;
        }
      },
      { onSuccess },
    );
  },
};

export default fitCheckingServices;
